import { routes } from "~/routes";
import { localeAware } from "~/utils/localeAware";

export function useInternalLinks(editorIsReadOnly: MaybeRefOrGetter<boolean>) {
  const { addTab, toTab } = useTabsMutations();
  const { data: tabs } = useFetchTabs();

  const anchorLegalTextSlug = ref<string>();
  const anchorParagraphSlug = ref<string>();
  const {
    query: { refetch: fetchAnchorParagraph },
  } = useFetchParagraphBySlug(anchorLegalTextSlug, anchorParagraphSlug);

  const buildTabRoute = useTabRoute();
  const { setActive } = useSetActiveEntry();
  const schemaId = ref<number>(-1);
  const schemaSlug = ref("");
  const { refetch: fetchSchemaBySlug } = useQuery({
    ...queries().entries.byType("schema")._ctx.bySlug(schemaSlug),
    enabled: false,
  });

  const { refetch: fetchSchemaById } = useQuery({
    ...queries().entries.byType("schema")._ctx.byId(schemaId),
    enabled: false,
  });

  const { workspace } = routes;
  const { withLocale } = localeAware(usePageContext());
  const navigate = useNavigate();

  return async (event): Promise<void> => {
    if (!toValue(editorIsReadOnly) || event.target.nodeName !== "A") {
      return;
    }

    const url = new URL(event.target.getAttribute("href"));
    const localizedPathname = withLocale(url.pathname);
    const isInternal = localizedPathname.startsWith(withLocale(workspace));
    if (!isInternal) {
      return;
    }

    event.stopImmediatePropagation();
    event.preventDefault();

    const [legalText, paragraph, maybeSchemaSlug] = localizedPathname
      .split("/")
      .slice(3);

    let fetchFn;
    if (maybeSchemaSlug) {
      schemaSlug.value = maybeSchemaSlug;
      fetchFn = fetchSchemaBySlug;
    } else if (url.searchParams.get("type") === "schema") {
      schemaId.value = Number(url.searchParams.get("id"));
      fetchFn = fetchSchemaById;
    }
    if (fetchFn) {
      const { data } = await fetchFn();
      if (!data) {
        return;
      }
      console.log("data", data);
      return setActive(data);
    }

    anchorLegalTextSlug.value = legalText;
    anchorParagraphSlug.value = paragraph;

    // if the tab exists, switch to it
    // if not, open a new one
    let nextTab = tabs.value.find(
      ({ legalText, paragraph }) =>
        legalText === anchorLegalTextSlug.value &&
        paragraph === anchorParagraphSlug.value
    );

    // load the tab if it does not exist
    if (!nextTab) {
      const { data } = await fetchAnchorParagraph();
      if (!data) {
        return;
      }
      nextTab = toTab(data.legal_text!, data);
      await addTab(nextTab);
    }

    // only paragraph, we need to stay with this schema
    const linkPath = buildTabRoute(nextTab);
    return navigate(linkPath);
  };
}
